// Dependency imports
import React, { ReactNode, useState, useEffect, useRef } from 'react';
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer as MuiTableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  styled,
  SortDirection,
  IconButton,
  Box,
  Skeleton
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import { visuallyHidden } from '@mui/utils';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';

// Local imports
import { setRowsPerPage } from 'store/pagination/paginationSlice';
import { RootState } from 'store';
import EnhancedTableToolbar from './enhancedSearch';

type Order = 'asc' | 'desc';

type RenderFunctionInput = {
  value: RecordValue;
  record: Record;
};

export const TableContainer = MuiTableContainer;

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText
}));

const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: 10
}));

const StyledTableHeadRow = styled(TableRow)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: theme.palette.primary.main
}));

export interface ITableCellProps {
  value: RecordValue;
  key: string;
  render?: (input: RenderFunctionInput) => ReactNode;
  align?: 'left' | 'right' | 'center';
  ellipsis?: boolean;
}

export interface ITableHeadColumn extends ITableCellProps {
  sorter?: boolean;
  sortOrder?: 'ascend' | 'descend';
  sortDirections?: SortDirection;
  width?: string;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof Record>(
  order: Order,
  orderBy: Key
): (a: Record, b: Record) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number): T[] {
  const stabilizedArray = array.map((el, index) => [el, index] as [T, number]);
  stabilizedArray.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedArray.map(el => el[0]);
}

interface EnhancedTableProps {
  columns: any;
  numSelected?: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  expandedRowRender: any;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort, columns, expandedRowRender } = props;
  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  console.log('expandedrowrender inside the table', expandedRowRender);

  return (
    <StyledTableHead
      sx={{
        width: '100%',
        backgroundColor: '#102633',
        '& .MuiTableCell-root': {
          color: 'white'
        },
        '& .MuiTableSortLabel-root': {
          color: 'white',
          '&.Mui-active': {
            color: 'white'
          },
          '&:hover': {
            color: 'gray'
          },
          '& .MuiTableSortLabel-icon': {
            color: 'white !important'
          }
        },
        '& .MuiCheckbox-root': {
          color: 'white'
        }
      }}
    >
      <StyledTableHeadRow>
        {columns.map((column: any) => (
          <StyledTableHeadCell
            key={column.key}
            variant="head"
            size={'medium'}
            align="left"
            sortDirection={orderBy === column.key ? order : false}
          >
            {column.key === 'id' || column.key === 'action' ? (
              <span>{column.value}</span>
            ) : (
              <TableSortLabel
                active={orderBy === column.key}
                direction={orderBy === column.key ? order : 'asc'}
                onClick={createSortHandler(column.key)}
                sx={{ fontSize: '0.9vw', width: 'fit-content' }}
              >
                {column.value}
                {orderBy === column.key ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
          </StyledTableHeadCell>
        ))}
        {expandedRowRender && <StyledTableHeadCell />}
      </StyledTableHeadRow>
    </StyledTableHead>
  );
}

export type RecordValue =
  | string
  | number
  | undefined
  | object
  | React.ReactElement;

export type Record = {
  id: string; // Ensure that each record has a unique id
  [key: string]: RecordValue | RecordValue[];
};

interface ITableProps {
  columns: any;
  rows: Array<Record>;
  expandable?: boolean;
  expandedRowRender?: (record: Record) => ReactNode;
  striped?: boolean; // Striped prop highlights row by setting different color on alternate rows
  loading?: boolean;
  stickyHeader?: boolean;
  containerStyle?: React.CSSProperties;
  highlightCondition?: (record: Record) => boolean; // Custom highlight condition
  size?: 'small' | 'medium';
  height?: string;
  disableHeader?: boolean;
  bordered?: boolean;
  expandedRowIndex?: any;
  setExpandedRowIndex?: any;
  setIsRowEditing?: any;
  searchVal?: string;
  setSearchVal?: any;
  showSearch?: boolean;
  page?: number;
  setPage?: React.Dispatch<React.SetStateAction<number>>;
}

const CellValue: React.FC<{
  key: string;
  value: RecordValue;
}> = ({ value }) => {
  if (React.isValidElement(value)) {
    return (
      <p
        className="!text-[0.8vw] min-w-fit"
        style={{ fontSize: '0.8vw', width: 'fit-content' }}
      >
        {value}
      </p>
    );
  }

  if (typeof value === 'object') {
    return (
      <p
        className="!text-[0.8vw] min-w-fit"
        style={{ fontSize: '0.8vw', width: 'fit-content' }}
      >
        {JSON.stringify(value)}
      </p>
    );
  }
  return (
    <p
      className="!text-[0.8vw] min-w-fit"
      style={{ fontSize: '0.8vw', width: 'fit-content' }}
    >
      {value}
    </p>
  );
};

interface IRowProps {
  record: Record;
  columns: any;
  expanded?: boolean;
  expandedRowRender?: (record: Record) => ReactNode;
  onExpandClick?: (expanded: boolean) => void;
  rowIndex: number; // Added rowIndex prop for indentify the odd and even rows on table
  striped?: boolean; // Striped prop highlights row by setting different color on alternate rows
  analysis?: RecordValue;
  highlightCondition?: (record: Record) => boolean; // Custom highlight condition
  size?: 'small' | 'medium';
}

// Define the component for rendering a row
const Row: React.FC<IRowProps> = ({
  record,
  columns,
  expanded = false,
  expandedRowRender,
  onExpandClick,
  rowIndex, // Added rowIndex prop for indentify the odd and even rows on table
  striped, // Striped prop highlights row by setting different color on alternate rows
  highlightCondition, // Custom highlight condition,
  size
}) => {
  const highlightRow = highlightCondition && highlightCondition(record);

  return (
    <>
      <TableRow
        className="!text-[0.8vw] "
        style={{
          fontSize: '0.8vw',
          backgroundColor: highlightRow
            ? '#90F893'
            : striped && rowIndex % 2 === 1
              ? '#E1E1E1'
              : 'inherit'
        }}
      >
        {columns.map((column: any, index: number) => (
          <TableCell
            key={`row-${index}`}
            size={size}
            className="!text-[0.8vw] p-0 pl-[10px] py-[1.3vh] min-w-fit"
            style={{
              fontSize: '0.8vw important',
              width: 'fit-content'
            }}
          >
            {column.render ? (
              column.render({ value: record[column.key], record })
            ) : (
              <CellValue key={record.toString()} value={record[column.key]} />
            )}
          </TableCell>
        ))}
        {expandedRowRender && (
          <TableCell className="!text-[0.8vw] py-[0.8vh]">
            <IconButton
              onClick={() => onExpandClick && onExpandClick(!expanded)}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </TableCell>
        )}
      </TableRow>
      {expanded && expandedRowRender && (
        <TableRow className="pl-[3%] !text-[0.8vw] py-[0.8vh]">
          <TableCell colSpan={columns.length + 1}>
            {expandedRowRender(record)}
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export const Table: React.FC<ITableProps> = ({
  columns,
  rows = [],
  expandedRowRender,
  striped = false,
  loading = false,
  stickyHeader = false,
  containerStyle = { height: '70vh' },
  highlightCondition,
  expandedRowIndex,
  setExpandedRowIndex,
  size = 'medium',
  setIsRowEditing,
  searchVal,
  setSearchVal,
  bordered,
  showSearch,
  page = 0,
  height = '70vh',
  setPage = () => {}
}: ITableProps) => {
  const [order, setOrder] = useState<Order>('asc');

  let data: string[] = [];
  const dispatch = useDispatch();
  const [orderBy, setOrderBy] = useState(data[0]);
  const [selected, setSelected] = useState<readonly number[]>([]);
  // const [page, setPage] = useState<number>(0);
  const [dense, setDense] = useState(false);
  const [rowsPerPage, setRowsInPage] = useState(
    useSelector((state: RootState) => state.pagination.rowsPerPage)
  );
  const [rowData, setRowData] = useState(rows);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    data = extractKeys(columns);
  }, []);

  useEffect(() => {
    setRowData(rows);
  }, [rows]);

  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [page]);

  console.log('table rerenering');

  const extractKeys = (data: any) => {
    return data.map((item: any) => item.key);
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setExpandedRowIndex(null);
    setIsRowEditing(false);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsInPage(parseInt(event.target.value, 10));
    dispatch(setRowsPerPage(event.target.value));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = React.useMemo(
    () =>
      page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rowData?.length) : 0,
    [rowData]
  );

  const visibleRows = React.useMemo(
    () =>
      stableSort(rowData, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, rowData]
  );

  return (
    <div className="w-full">
      {showSearch && (
        <EnhancedTableToolbar
          setRow={setRowData}
          searchVal={searchVal}
          setSearchVal={setSearchVal}
          rows={rows}
          setExpandedRowIndex={setExpandedRowIndex}
          setPage={setPage}
          setIsRowEditing={setIsRowEditing}
        />
      )}
      <TableContainer
        ref={tableContainerRef}
        style={containerStyle}
        className={`scrollbar scrollbar-track-gray-100 scrollbar-thumb-[#102433] hover:scrollbar-thumb-[#1c3a4f] scrollbar-thumb-rounded-full scrollbar-track-rounded-full overflow-auto`}
      >
        <MuiTable stickyHeader={stickyHeader} className="w-full">
          <EnhancedTableHead
            // numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            expandedRowRender={expandedRowRender}
            rowCount={rows.length || 0}
            columns={columns}
          />
          <TableBody className='overflow-y-auto'>
            {loading &&
              [1, 2, 3, 4, 5, 6].map((_, index) => (
                <TableRow key={`loading-row-${index}`}>
                  {columns.map(() => (
                    <TableCell
                      key={`loading-cell-${index}-${Math.floor(
                        Math.random() * 1000000
                      )}`}
                      size={size}
                      className="p-0"
                    >
                      <Skeleton variant="text" className="h-[8vh] mr-1" />
                    </TableCell>
                  ))}
                  {expandedRowRender && (
                    <TableCell
                      key={`loading-extracell-${index}`}
                      size={size}
                      className="p-0"
                    >
                      <Skeleton variant="text" className="h-[8vh] mr-1" />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            {!loading &&
              visibleRows.map((row, rowIndex) => {
                return (
                  <Row
                    key={`row-${row.id}-${rowIndex}`}
                    record={row}
                    size={size}
                    analysis={row.analysis}
                    columns={columns}
                    expandedRowRender={expandedRowRender}
                    onExpandClick={expanded => {
                      setExpandedRowIndex(expanded ? rowIndex : null);
                    }}
                    expanded={expandedRowIndex === rowIndex}
                    rowIndex={rowIndex}
                    striped={striped}
                    highlightCondition={highlightCondition}
                  />
                );
              })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: (dense ? 33 : 53) * emptyRows
                }}
              >
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </MuiTable>
        {visibleRows?.length == 0 && !loading && (
          <p
            style={{
              width: '100%',
              height: '50vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            No Records Found
          </p>
        )}
      </TableContainer>
      <TablePagination
        className=""
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={rowData.length || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
};
